import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, Mail } from 'lucide-react';
import { Container, Button } from '../ui';
import { useIntersectionObserver } from '../../hooks';
import { CONTACT_INFO } from '../../constants';

export const CTA: React.FC = () => {
  const { elementRef, isVisible } = useIntersectionObserver({ threshold: 0.2 });

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900" />
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      {/* Gradient Orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-navy-400/20 rounded-full blur-3xl" />

      <Container className="relative z-10">
        <motion.div
          ref={elementRef}
          className="text-center max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.6 }}
        >
          {/* Heading */}
          <h2 className="heading-lg text-white mb-6">
            Ready to Transform Your
            <span className="block text-accent-400">Technology Landscape?</span>
          </h2>

          {/* Description */}
          <p className="text-xl text-navy-200 mb-10 leading-relaxed">
            Let's discuss how ISTATEC can accelerate your digital transformation 
            with expert consulting, development, and technical talent.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                variant="accent"
                size="lg"
                leftIcon={<MessageCircle className="w-5 h-5" />}
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Start a Conversation
              </Button>
            </Link>
            <a href={`mailto:${CONTACT_INFO.email}`}>
              <Button
                variant="ghost"
                size="lg"
                className="text-white hover:bg-white/10 border border-white/20"
                leftIcon={<Mail className="w-5 h-5" />}
              >
                Email Us
              </Button>
            </a>
          </div>

          {/* Trust Indicators */}
          <motion.div
            className="mt-12 flex flex-wrap items-center justify-center gap-8 text-navy-300 text-sm"
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-400 rounded-full" />
              No commitment required
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-400 rounded-full" />
              Free consultation
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-400 rounded-full" />
              Response within 24 hours
            </div>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
};

